export interface PageSEO {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  canonicalPath?: string;
}

export const seoMetadata: Record<string, PageSEO> = {
  home: {
    title: "AI Ecommerce Platform - Automate Marketing & Operations",
    description: "thinker is an AI-powered ecommerce platform that automates ad generation, campaign management, and store optimization. Grow faster with 10x creative production and 24/7 autonomous operations.",
    keywords: "AI ecommerce, marketing automation, ecommerce growth, ad generation, campaign automation, AI marketing platform",
    h1: "Ecommerce growth at lightspeed",
    canonicalPath: "/",
  },
  products: {
    title: "AI Marketing Automation Platform for Ecommerce",
    description: "Accelerate growth with AI that generates traffic, converts visitors, and optimizes operations. Full-funnel marketing automation with 24/7 campaign management and creative production.",
    keywords: "AI marketing tools, ad generation, campaign automation, ecommerce automation, marketing platform, AI ads",
    h1: "AI-Powered Marketing Automation Platform",
    canonicalPath: "/products",
  },
  pricing: {
    title: "Pricing Plans - AI Ecommerce Platform",
    description: "Simple, transparent pricing for AI-powered ecommerce automation. Choose from Starter, Pro, or Enterprise plans. Start free and scale as you grow.",
    keywords: "ecommerce automation pricing, AI marketing cost, thinker pricing, marketing automation plans",
    h1: "Pricing Plans",
    canonicalPath: "/pricing",
  },
  about: {
    title: "About Autonomous Ecommerce - Our Mission",
    description: "Learn about thinker's mission to bring autonomous ecommerce to every business. We're building AI systems that handle marketing, operations, and growth—so you can focus on your product.",
    keywords: "autonomous ecommerce, AI-powered retail, thinker mission, ecommerce automation company",
    h1: "The Dawn of Autonomous Ecommerce",
    canonicalPath: "/about",
  },
  contact: {
    title: "Contact Us - Book a Demo",
    description: "Get in touch with thinker to see how AI can automate your ecommerce marketing and operations. Book a demo, schedule a call, or send us a message.",
    keywords: "thinker demo, ecommerce AI consultation, contact thinker, book demo",
    h1: "Contact Us - Start Your AI Marketing Journey",
    canonicalPath: "/contact",
  },
  editorial: {
    title: "AI Ecommerce Insights and Strategies",
    description: "Expert insights on AI-powered ecommerce, marketing automation, and operational efficiency. Learn strategies to grow faster with less overhead.",
    keywords: "AI marketing articles, ecommerce trends, marketing automation insights, AI ecommerce strategies",
    h1: "AI Ecommerce Insights and Marketing Strategies",
    canonicalPath: "/editorial",
  },
  webinars: {
    title: "AI Ecommerce Webinars and Training",
    description: "Watch free webinars on AI-powered ecommerce strategies. Learn about customer acquisition, retention, conversion optimization, and creative automation.",
    keywords: "ecommerce webinars, AI marketing training, thinker webinars, marketing automation training",
    h1: "AI Ecommerce Webinars and Training Resources",
    canonicalPath: "/webinars",
  },
  help: {
    title: "Help Center - Getting Started Guide",
    description: "Get started with thinker's AI ecommerce platform. Step-by-step guides, tutorials, and support resources to help you automate your marketing and operations.",
    keywords: "thinker help, ecommerce automation setup, getting started guide, thinker support",
    h1: "Help Center - thinker Setup and Support",
    canonicalPath: "/help",
  },
  careers: {
    title: "Join Our Team - Careers at thinker",
    description: "Join thinker and help build the future of autonomous ecommerce. We're looking for passionate people who want to revolutionize how businesses grow.",
    keywords: "thinker careers, AI startup jobs, ecommerce jobs, marketing technology careers",
    h1: "Join the Team Building Autonomous Ecommerce",
    canonicalPath: "/careers",
  },
  agencies: {
    title: "AI Marketing Automation for Agencies",
    description: "Scale your agency with AI-powered marketing automation. Manage multiple clients, generate bulk creative, and deliver white-label reports—all from one platform.",
    keywords: "agency marketing tools, white-label AI, multi-client management, agency automation, bulk creative generation",
    h1: "AI Marketing Automation for Agencies",
    canonicalPath: "/solutions/agencies",
  },
  smallBusinesses: {
    title: "AI Marketing for Small Business",
    description: "Enterprise-level marketing automation at small business prices. Get AI-powered ad generation, campaign management, and analytics without the enterprise cost.",
    keywords: "small business marketing automation, affordable AI marketing, SMB ecommerce tools, marketing for small business",
    h1: "Enterprise Marketing Power for Small Business",
    canonicalPath: "/solutions/small-businesses",
  },
  dtcBrands: {
    title: "AI Creative Production for DTC Brands",
    description: "Scale your DTC brand with AI-powered creative production and conversion optimization. Generate hundreds of ad variations, optimize product pages, and grow faster.",
    keywords: "DTC marketing, creative automation, direct-to-consumer ads, DTC growth, ad creative generation",
    h1: "AI Creative Production for DTC Brands",
    canonicalPath: "/solutions/dtc-brands",
  },
};

export const articleSEO = {
  aiReplacesMarketing: {
    title: "Why 2026 is the Year AI Replaces Marketing Teams",
    description: "By 2026, AI will handle 80% of marketing tasks currently done by humans. Learn how businesses are already making the transition and what it means for your team.",
    keywords: "AI marketing, marketing automation, AI replaces marketers, future of marketing, marketing jobs AI",
  },
  campaignMonitoring: {
    title: "Stop Losing Money to Manual Campaign Monitoring",
    description: "Manual campaign monitoring costs businesses $50K-200K annually in missed opportunities. Learn how AI-powered monitoring catches issues in real-time.",
    keywords: "campaign monitoring, ad monitoring, marketing automation, real-time alerts, campaign optimization",
  },
  dataSilos: {
    title: "The Hidden Tax of Data Silos in E-commerce Marketing",
    description: "Data silos cost the average e-commerce business 23% of their marketing budget. Learn how unified data platforms eliminate waste and improve attribution.",
    keywords: "data silos, marketing attribution, unified data, ecommerce analytics, marketing data platform",
  },
  marketingAutomation: {
    title: "5 Marketing Tasks You Should Automate Today",
    description: "Stop doing manual marketing work. These 5 tasks can be automated today, saving 20+ hours per week and improving results by 40%.",
    keywords: "marketing automation, automate marketing, marketing tasks, time savings, marketing efficiency",
  },
  manualWork: {
    title: "Why Manual Work Is the Enemy of Profitable Growth",
    description: "Manual work creates a ceiling on profitability. Learn the automation-first approach that enables 3x margins on the same revenue.",
    keywords: "manual work, automation, profitable growth, ecommerce efficiency, operational costs",
  },
  aiCreative: {
    title: "The Art of AI-Generated Creative: Best Practices for High-Converting Ads",
    description: "Master AI-generated ad creative with these proven best practices. Learn how top brands achieve 3x output with the same team size.",
    keywords: "AI creative, ad generation, AI ads, creative automation, ad creative best practices",
  },
  operationsEfficiency: {
    title: "From 10 Employees to 1: The New Math of E-commerce Operations",
    description: "The traditional scaling formula is broken. Learn how automation-first businesses achieve $5M revenue with 4 people instead of 20.",
    keywords: "ecommerce operations, automation, headcount reduction, operational efficiency, team scaling",
  },
};
